import { useState } from 'react';
import { ColumnDef } from '@/components/data-table';
import { Avatar } from '@/components/ui';
import { maskPhone } from '@/utils';
import { formatDateWithDifference } from '@/utils/transformer';
import { UserCircle, Plus, Check, Copy, BadgeCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Botão copiar ID: mesma coluna que LINK MEET (só o botão na célula). */
export const CopyIdButton = ({ id }: { id: string }) => {
	const [isCopied, setIsCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		navigator.clipboard.writeText(id);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={cn(
				'flex cursor-pointer items-center gap-1.5 rounded border border-transparent px-2 py-1 text-[10px] font-bold uppercase tracking-tight outline-none transition-all focus:outline-none',
				isCopied
					? 'border-emerald-100 bg-emerald-50 text-emerald-600'
					: 'bg-indigo-50 text-indigo-600 hover:border-indigo-200 hover:bg-indigo-100',
			)}
			title={isCopied ? 'ID copiado!' : 'Clique para copiar o ID'}
		>
			{isCopied ? (
				<Check
					size={12}
					strokeWidth={3}
					className="animate-in zoom-in duration-300"
				/>
			) : (
				<Copy size={12} strokeWidth={2.5} />
			)}
			<span>{isCopied ? 'COPIADO!' : 'ID'}</span>
		</button>
	);
};

interface UserColumnsSessionProps {
	onSelect: (user: Users.User) => void;
	/**
	 * IDs dos usuários selecionados. Quando a linha atual estiver incluída,
	 * o botão mostra Check em vez de +.
	 */
	selectedUserIds?: string[];
	/** 'participantsOnly' = ID, Usuário, Status... (ex.: página de participantes da sessão) */
	/** 'selectPatient' = como full mas sem Telefone e Data nascimento (ex.: agendar consulta) */
	variant?: 'full' | 'participantsOnly' | 'selectPatient';
	/** Lista de clubes para resolver nome por club_id (fallback quando API não retorna club) */
	clubs?: Clubs.Club[];
}

const selectColumn = (
	onSelect: (user: Users.User) => void,
	selectedUserIds: string[] | undefined,
): ColumnDef<Users.User, keyof Users.User> => ({
	header: 'Selecionar',
	accessor: 'id',
	cell: (_, row) => {
		const isSelected =
			Array.isArray(selectedUserIds) && selectedUserIds.includes(row.id);
		return (
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onSelect(row);
				}}
				className={cn(
					'flex h-9 w-9 items-center justify-center rounded-xl border border-solid outline-none transition focus:outline-none',
					isSelected
						? 'border-zinc-700 bg-zinc-700 text-white shadow-sm hover:border-zinc-800 hover:bg-zinc-800'
						: 'border-stone-300 bg-white text-zinc-600 hover:border-stone-400 hover:bg-stone-50',
				)}
			>
				{isSelected ? (
					<Check size={20} strokeWidth={3} />
				) : (
					<Plus size={20} strokeWidth={2.5} />
				)}
				<span className="sr-only">
					{isSelected ? 'Selecionado' : 'Selecionar'}
				</span>
			</button>
		);
	},
});

const UserColumnsSession = ({
	onSelect,
	selectedUserIds,
	variant = 'full',
	clubs = [],
}: UserColumnsSessionProps): ColumnDef<Users.User, keyof Users.User>[] => {
	const clubById = Object.fromEntries(clubs.map((c) => [c.id, c]));
	const idColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'ID',
		accessor: 'id',
		cell: (_, row) => <CopyIdButton id={row.id} />,
	};

	const userColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Usuário',
		accessor: 'first_name',
		cell: (_, row) => (
			<div className="flex items-center">
				{row.profile_photo_url ? (
					<Avatar
						className="mr-3 h-8 w-8 shrink-0"
						variant="circle"
						img_url={row.profile_photo_url}
					/>
				) : (
					<div className="mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-400">
						<UserCircle className="h-5 w-5" />
					</div>
				)}
				<div className="min-w-0 truncate">
					<p className="truncate font-medium text-dark">
						{row.first_name} {row.last_name}
					</p>
					<p className="flex min-w-0 items-center gap-1 text-xs text-gray-500">
						<span className="truncate">{row.email}</span>
						{row.verified_email && (
							<span title="Email verificado">
								<BadgeCheck
									className="h-4 w-4 shrink-0 text-emerald-500"
									strokeWidth={2}
									aria-label="Email verificado"
								/>
							</span>
						)}
					</p>
				</div>
			</div>
		),
	};

	const phoneColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Telefone',
		accessor: 'phone_number',
		cell: (value) => (
			<span className="text-dark">
				{value ? maskPhone(value as string) : ''}
			</span>
		),
	};

	const statusColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Status',
		accessor: 'banned',
		cell: (value) => (
			<span
				className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
					value
						? 'bg-red-100 text-red-800'
						: 'bg-green-100 text-green-800'
				}`}
			>
				{value ? 'Banido' : 'Ativo'}
			</span>
		),
	};

	const cadastroColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Cadastro',
		accessor: 'is_complete',
		cell: (value) => (
			<span
				className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
					value
						? 'bg-blue-100 text-blue-800'
						: 'bg-yellow-100 text-yellow-800'
				}`}
			>
				{value ? 'Completo' : 'Incompleto'}
			</span>
		),
	};

	const googlePermissionsColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Permissões Google',
		accessor: 'google_permissions_allowed',
		cell: (value) => (
			<span
				className={cn(
					'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
					value
						? 'bg-emerald-100 text-emerald-800'
						: 'bg-gray-100 text-gray-600',
				)}
			>
				{value ? 'Sim' : 'Não'}
			</span>
		),
	};

	const clubeColumn: ColumnDef<Users.User, keyof Users.User> = {
		header: 'Clube',
		accessor: 'user_club_subscription',
		cell: (_, row) => {
			const subs = row.user_club_subscription ?? [];
			const clubNames = subs
				.map((s) => s.club?.name ?? clubById[s.club_id]?.name)
				.filter(Boolean);
			const label = clubNames.length > 0 ? clubNames.join(', ') : '—';
			return (
				<span
					className={`inline-flex max-w-[120px] items-center truncate rounded-full px-2.5 py-0.5 text-xs font-medium ${
						clubNames.length > 0
							? 'bg-purple-100 text-purple-800'
							: 'bg-gray-100 text-gray-800'
					}`}
					title={label}
				>
					{label}
				</span>
			);
		},
	};

	if (variant === 'participantsOnly') {
		return [
			idColumn,
			userColumn,
			statusColumn,
			cadastroColumn,
			clubeColumn,
			selectColumn(onSelect, selectedUserIds),
		];
	}

	const baseColumns: ColumnDef<Users.User, keyof Users.User>[] = [
		idColumn,
		userColumn,
	];

	const fullColumns: ColumnDef<Users.User, keyof Users.User>[] = [
		...baseColumns,
		statusColumn,
		cadastroColumn,
		clubeColumn,
		phoneColumn,
		{
			header: 'Data nascimento',
			accessor: 'birthday',
			cell: (value) => (
				<span className="text-dark">
					{formatDateWithDifference(value as string)}
				</span>
			),
		},
		selectColumn(onSelect, selectedUserIds),
	];

	if (variant === 'selectPatient') {
		return [
			...baseColumns,
			statusColumn,
			cadastroColumn,
			googlePermissionsColumn,
			clubeColumn,
			selectColumn(onSelect, selectedUserIds),
		];
	}

	return fullColumns;
};

export { UserColumnsSession };
